export const SIDEBAR_WIDTH = 400;
/** Below this viewport width the sidebar overlays the page instead of pushing it. */
export const PUSH_MIN_VIEWPORT = 768;

export type Side = 'right' | 'left';

export interface Shift {
  translate?: string;
  maxWidth?: string;
}

interface Box {
  left: number;
  right: number;
  width: number;
  height: number;
}

/**
 * Decides how a `position: fixed` element must move so it stays clear of the sidebar.
 * - Spans the full width (headers, backdrops): cap its width to the remaining space.
 * - Centred (modals, toasts): move it half the sidebar width, so it is centred in the remaining space.
 * - Anchored to the sidebar's edge (drawers, chat buttons): move it the full sidebar width.
 * - Clear of the sidebar zone: leave it alone.
 */
export function classifyFixed(
  box: Box,
  viewportWidth: number,
  width: number,
  side: Side,
): Shift | null {
  if (box.width === 0 && box.height === 0) return null;
  const overlaps = side === 'right' ? box.right > viewportWidth - width : box.left < width;
  if (!overlaps) return null;
  if (box.left <= 1 && box.right >= viewportWidth - 1)
    return { maxWidth: `calc(100% - ${width}px)` };
  const sign = side === 'right' ? -1 : 1;
  const centred = Math.abs((box.left + box.right) / 2 - viewportWidth / 2) < 2;
  return { translate: `${sign * (centred ? width / 2 : width)}px 0` };
}

export interface PushLayout {
  open(): void;
  close(): void;
}

/**
 * Makes room for the sidebar by adding a margin to <html>, so the page reflows into the remaining
 * width. Fixed elements ignore that margin, so they are found and shifted separately, and found
 * again whenever the DOM changes (a drawer that opens after the sidebar, for example).
 *
 * Also exposes `--hoppinger-feedback-width` on <html> for host apps that prefer to handle
 * their own fixed elements.
 */
export function createPushLayout(options: {
  width: number;
  side: Side;
  exclude: Element;
}): PushLayout {
  const { width, side, exclude } = options;
  const root = document.documentElement;
  const marginProp = side === 'right' ? 'marginRight' : 'marginLeft';
  const shifted = new Map<HTMLElement, { translate: string; maxWidth: string }>();
  let active = false;
  let previousMargin = '';
  let observer: MutationObserver | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const restoreAll = () => {
    for (const [el, previous] of shifted) {
      el.style.translate = previous.translate;
      el.style.maxWidth = previous.maxWidth;
    }
    shifted.clear();
  };

  const scan = () => {
    if (!active || !document.body) return;
    for (const el of shifted.keys()) if (!el.isConnected) shifted.delete(el);
    const viewportWidth = root.clientWidth;
    // Document order, so a shifted parent moves its fixed children before they are measured.
    for (const el of Array.from(document.body.getElementsByTagName('*'))) {
      if (!(el instanceof HTMLElement) || el === exclude || shifted.has(el)) continue;
      if (getComputedStyle(el).position !== 'fixed') continue;
      const shift = classifyFixed(el.getBoundingClientRect(), viewportWidth, width, side);
      if (!shift) continue;
      shifted.set(el, { translate: el.style.translate, maxWidth: el.style.maxWidth });
      if (shift.translate) el.style.translate = shift.translate;
      if (shift.maxWidth) el.style.maxWidth = shift.maxWidth;
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(scan, 100);
  };

  const onResize = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      restoreAll();
      scan();
    }, 150);
  };

  return {
    open() {
      if (active) return;
      active = true;
      previousMargin = root.style[marginProp];
      root.style[marginProp] = `${width}px`;
      root.style.setProperty('--hoppinger-feedback-width', `${width}px`);
      scan();
      observer = new MutationObserver(schedule);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open'],
      });
      window.addEventListener('resize', onResize);
    },
    close() {
      if (!active) return;
      active = false;
      clearTimeout(timer);
      observer?.disconnect();
      observer = null;
      window.removeEventListener('resize', onResize);
      restoreAll();
      root.style[marginProp] = previousMargin;
      root.style.removeProperty('--hoppinger-feedback-width');
    },
  };
}
